import React, { useEffect, useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { Avatar, notification } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import usePaquers from 'redux/paquerList';
import { actions } from 'redux/paquerList/slice';
import {
  paquersFilteredItemsSelector,
  paquersLoadedSelector,
  paquersItemsSelector,
} from 'redux/paquerList/selectors';

import { ExportDateFormat } from 'constants/dateFormats';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { PERMISSIONS, hasPermission } from 'constants/roles';
import API from 'constants/api';
import PaqueryTable, { TableHeader } from '@paquery-team/lib-table';
import CsvDownloader from 'util/CsvDownloader';
import rest from 'util/Api';
import store from 'redux/configureStore';
import { authProfile } from 'redux/auth/selectors';
import { vehicleTypesGlobalsSelector } from 'redux/globals/selectors';
import ViewDriverButton from 'components/viewDriverButton';
import DeleteModal from 'components/deleteModal';
import DeleteRecordButton from 'components/deleteRecordButton';

const avatarColumn = {
  title: 'Avatar',
  render: (_, record) => <Avatar icon={<UserOutlined />} src={record.avatar} />,
};

const nameColumn = {
  title: 'Nombre',
  dataIndex: 'name',
};

const lastNameColumn = {
  title: 'Apellido',
  dataIndex: 'lastName',
};

const emailColumn = {
  title: 'Usuario',
  dataIndex: 'email',
};

const activeColumn = {
  title: 'Estado',
  dataIndex: 'active',
};

const getActiveStatus = (status) => {
  if (status === 1) return 'Activo';
  if (status === 6) return 'Inactivo';
  return false;
};

const viewPaquerColumn = {
  title: 'Ver',
  align: 'center',
  render: (_, record) => <ViewDriverButton driverId={record.id} />,
};

const mediumDeviceColumns = [nameColumn, lastNameColumn, viewPaquerColumn];

const columnsSmallDevice = [nameColumn, lastNameColumn, viewPaquerColumn];

const deleteAction = (paquerId, paquerName) => {
  const deleteRequest = async () => {
    try {
      const response = await rest.del(`${API.paquer.delete}${paquerId}`);
      if (response.status === 204) {
        store.dispatch({ type: actions.refreshPage.type });
      }
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      notification.error({
        message: 'Ha ocurrido un error al eliminar el paquer',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
      return undefined;
    }
  };
  DeleteModal({
    title: `¿Estas seguro de que deseas eliminar a ${paquerName}`,
    onConfirmation: deleteRequest,
  });
};

const deletePaquerColumn = {
  title: 'Eliminar',
  align: 'center',
  render: (_, record) => (
    <DeleteRecordButton
      onClick={() =>
        deleteAction(record.key, `${record.name} ${record.lastName}`)
      }
    />
  ),
};

const PaquerList = () => {
  usePaquers();
  const dispatch = useDispatch();
  const profile = useSelector(authProfile);
  const canAlterPaquer = hasPermission(profile, PERMISSIONS.AddEditPaquer);
  const loaded = useSelector(paquersLoadedSelector);
  const fullItems = useSelector(paquersItemsSelector);
  const vehicles = useSelector(vehicleTypesGlobalsSelector);
  const { items, pageable } = useSelector(paquersFilteredItemsSelector);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    dispatch(actions.initial());
  }, [dispatch]);
  const getCsv = async () => {
    setDisabled(true);
    try {
      let csv = fullItems;
      csv = csv.map((paquer) => ({
        ...paquer,
        active: getActiveStatus(paquer.status),
        vehicleTypeID: paquer.vehicleTypeID
          ? vehicles.filter((type) => type.id === paquer.vehicleTypeID)[0].name
          : null,
      }));
      const csvHeader = [
        { label: 'Nombre', value: 'name' },
        { label: 'Apellido', value: 'lastName' },
        { label: 'Email', value: 'email' },
        { label: 'Número de documento', value: 'docNumber' },
        { label: 'Teléfono', value: 'mobile' },
        { label: 'Vehículo', value: 'vehicleTypeID' },
        { label: 'Estado', value: 'active' },
      ];
      const filename = `paquers-${dayjs().tz().format(ExportDateFormat)}.csv`;
      CsvDownloader(csv, csvHeader, filename);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      notification.error({
        message: 'Ha ocurrido un error al traer los paquers',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
    } finally {
      setDisabled(false);
    }
  };
  let filteredPaquers;
  if (loaded && items) {
    filteredPaquers = items.map((paquer) => ({
      ...paquer,
      key: paquer.id,
      name: paquer.name,
      lastName: paquer.lastName,
      avatar: paquer.avatar,
      email: paquer.email,
      active: getActiveStatus(paquer.status),
    }));
  }
  const searchCallback = useCallback(
    (value) => {
      dispatch(actions.updateSearch(value));
    },
    [dispatch],
  );

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePageable(page));
    },
    [dispatch],
  );
  const searcher = {
    key: 'searchCodeOrDestinationName',
    onSearching: searchCallback,
    placeholder: 'Nombre o email',
    allowEmptySearch: true,
  };

  const vehicleTypeColumn = {
    title: 'Vehículo',
    dataIndex: 'vehicleTypeID',
    render: (vehicleTypeID) =>
      vehicles.find((type) => type.id === vehicleTypeID)?.name ?? null,
  };

  // El botón "Ver" abre el detalle, que es también el formulario de edición
  // cuando el rol tiene permiso. Sólo el borrado es una columna aparte.
  const extraColumns = canAlterPaquer ? [deletePaquerColumn] : [];

  const dataColumns = [
    nameColumn,
    lastNameColumn,
    emailColumn,
    vehicleTypeColumn,
    activeColumn,
    viewPaquerColumn,
    ...extraColumns,
  ];

  const fullSizeColumns = [
    avatarColumn,
    nameColumn,
    lastNameColumn,
    emailColumn,
    vehicleTypeColumn,
    activeColumn,
    viewPaquerColumn,
    ...extraColumns,
  ];

  const header = {
    title: 'Paquers',
    searcher,
    refresh: () => dispatch(actions.refreshPage()),
    onExportCsv: { callback: getCsv, disabled },
  };
  if (canAlterPaquer) {
    header.primaryButton = <TableHeader.AddButton url="/paquers/add" />;
  }

  return (
    <PaqueryTable
      loading={!loaded}
      header={header}
      onChangePaginate={updatePaginate}
      dataSource={filteredPaquers}
      paginate={pageable || DEFAULT_PAGINATE}
      dataColumns={dataColumns}
      colsForMediumDevice={[...mediumDeviceColumns, ...extraColumns]}
      colsForSmallDevice={[...columnsSmallDevice, ...extraColumns]}
      colsForLargeDevice={fullSizeColumns}
    />
  );
};

export default PaquerList;
